<script setup lang="ts">
  import VisitorForm from '@/shared/components/rebranding/VisitorForm/VisitorForm.vue'
  import { getAbTest } from '@/shared/composables/useGetAbTest'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { usePerformanceTiming } from '@/shared/composables/usePerformanceTiming'
  import { usePrepareInboundAddon } from '@/shared/composables/usePrepareInboundAddon'
  import { useVisaPreapproval } from '@/shared/composables/useVisaPreapproval'
  import {
    GENDERS,
    INBOUND_ADDON_CODE,
    INBOUND_ADDON_KEYS,
    OWNER_RELATION,
  } from '@/shared/constants/other.constants'
  import {
    emailResponse,
    inboundCheckResponse,
  } from '@/shared/constants/validationMessages.constants'
  import Visitor from '@/shared/models/Visitor'
  import { useCoreStore, useVisitorsStore } from '@/shared/store'
import { CountrysResponse } from '@/shared/types/response.types'
  import { PurchaseFlowRoutes } from '@/shared/types/router.types'
  import { VisitorType } from '@/shared/types/store.types'
  import { formatDate } from '@/shared/utils'
  import { pushEvent } from '@/shared/utils/push-event'
  import { toPolicyTimestamp } from '@qicgroupdoha/travel-devkit/helpers/dates'
  import { computed, onMounted, ref } from 'vue'

  interface Emits {
    (e: 'proceed', value: string): void
    (e: 'load', value: () => Promise<void>, requestId?: string, withLoader?: boolean): void
    (e: 'error', value: string): void
  }

  const emit = defineEmits<Emits>()

  const visitorsStore = useVisitorsStore()
  const coreStore = useCoreStore()
  const gaService = useGoogleAnalytics()
  const { onPerformanceCheckEnd } = usePerformanceTiming()
  const { checkPrepareInboundAddon } = usePrepareInboundAddon()
  const { getPreapprovalVisitor, hasPreapprovalVisa } = useVisaPreapproval()
  const isLoading = ref(false)
  const emailIsValid = ref(true)

  const holder = computed(() => visitorsStore.holder)
  const countries = computed(() => coreStore.countries)
  const visitDates = computed(() => visitorsStore.getVisitDates)

  const entryType = visitorsStore.getAnalyticsEntryType

  const getVisitorNextPage = (visitor: VisitorType): string =>
    hasPreapprovalVisa(visitor) ? PurchaseFlowRoutes.TRAVEL_DURATION : PurchaseFlowRoutes.QUOTE

  const findCountryByCode = (code: Nullable<string>) =>
    countries.value.find((c) => c.code === code)

  const formatIsoDate = (date: Nullable<Date> | undefined): string | undefined =>
    date ? formatDate(new Date(date), 'en', 'yyyy-MM-dd') : undefined

  const buildFormSubmitParams = (holder: VisitorType, country?: CountrysResponse): Record<string, string | undefined> => ({
    product: 'visitors',
    relationship: holder.relationshipName,
    first_name: holder.firstName,
    family_name: holder.lastName,
    full_name: holder.fullName ?? `${holder.firstName} ${holder.lastName}`.trim(),
    date_of_birth: formatIsoDate(holder.birthDate),
    gender: holder.gender === GENDERS.MALE ? 'male' : 'female',
    nationality: holder.nationalityName ?? undefined,
    country_of_residence: country?.desc,
    passport_number: holder.passport,
    passport_expiry_date: formatIsoDate(holder.passportExpiryDate),
    visa_no: holder.visa,
    visa_expiry_date: formatIsoDate(holder.visaValidityDate),
    email: holder.email,
    phone_number: holder.phoneInfo.phone,
  })

  const proceed = async (holderInfo: VisitorType) => {
    emit(
      'load',
      async () => {
        let holder = {
          ...holderInfo,
          relationship: OWNER_RELATION.CODE,
          relationshipName: OWNER_RELATION.NAME,
          srNo: holderInfo.srNo === 0 ? 1 : holderInfo.srNo,
        }
        isLoading.value = true

        holder = await getPreapprovalVisitor(holder)

        const country = findCountryByCode(holder.country)
        const gender = holder.gender === GENDERS.MALE ? 'male' : 'female'

        await visitorsStore
          .updateVisitor(new Visitor(holder, holder.id))
          .then(() => {
            gaService.sendClickProceedByHolderInfo(
              entryType,
              gender,
              country?.desc || '',
              coreStore.getInitialReferrer,
              holder.passport.substring(holder.passport.length - 4)
            )

            pushEvent({
              event_name: 'form_submit',
              params: buildFormSubmitParams(holder, country),
            })
          })
          .catch((error) => {
            const message = error?.response.data.errorMessage
            emailIsValid.value = message !== emailResponse
            visitorsStore.getTravellers()
          })

        if (!holder.visaValidityDate) {
          if (checkPrepareInboundAddon(holder, visitorsStore.entryValue)) {
            await getAbTest(INBOUND_ADDON_CODE)
          }
        }

        if (emailIsValid.value) {
          const isInboundAddonAbTest = INBOUND_ADDON_KEYS.includes(
            coreStore.getAbValue(INBOUND_ADDON_CODE)
          )
          if (
            isInboundAddonAbTest &&
            !holder.visaValidityDate &&
            checkPrepareInboundAddon(holder, visitorsStore.entryValue)
          ) {
            const res = await visitorsStore
              .inboundAddonCheck(
                holder.passport,
                visitDates.value.startDate,
                visitDates.value.tillDate
              )
              .catch((error) => {
                const errorCode = error?.response?.data?.errorCode
                if (errorCode !== inboundCheckResponse) {
                  emit('error', error)
                }
              })
            visitorsStore.setInboundAddon(res?.success || false)
          } else {
            visitorsStore.setInboundAddon(false)
          }
          emit('proceed', getVisitorNextPage(holder))
        }

        isLoading.value = false
      },
      'holderInfo',
      false
    )
  }

  const sendFormData = async (startDate: Date, entryId: string, numadults = 1) => {
    const payload: {
      policyDate: number
      travelType: string
      numadults: number
      nationalityCode?: string
      nationalityName?: string
    } = {
      policyDate: toPolicyTimestamp(startDate),
      travelType: entryId,
      numadults,
    }

    if (visitorsStore.lastHolderNationality) {
      payload.nationalityCode = visitorsStore.lastHolderNationality
    }

    if (visitorsStore.lastHolderNationalityName) {
      payload.nationalityName = visitorsStore.lastHolderNationalityName
    }

    await visitorsStore.sendFormDataStepped(payload)
  }

  const checksInfo = async () => {
    await Promise.all([
      sendFormData(
        visitorsStore.visitDate,
        visitorsStore.entryValue,
        visitorsStore.numberOfVisitors
      ),
      coreStore.loadStoreData(),
      visitorsStore.getTravellers(),
    ])
  }

  onMounted(() => {
    emit(
      'load',
      async () => {
        await checksInfo()
        onPerformanceCheckEnd()
      },
      'holder-info-load'
    )
  })
</script>

<template>
  <VisitorForm
    :visitor="holder"
    :email-is-valid="emailIsValid"
    :is-loading="isLoading"
    is-holder
    @save="proceed"
    @set-email-is-valid="emailIsValid = true"
  />
</template>
